/** 
  All of the routes for the MindPlus are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// MindPlus layouts
import Dashboard from "layouts/dashboard";
import Sales from "layouts/dashboard2";
import ProfileOverview from "layouts/pages/profile/profile-overview";
import AllProjects from "layouts/pages/profile/all-projects";
import NewUser from "layouts/users/new-user";
import Settings from "layouts/users/settings";
import Billing from "layouts/pages/account/billing";
import Invoice from "layouts/pages/account/invoice";
import Timeline from "layouts/pages/projects/timeline";
import PricingPage from "layouts/pages/pricing-page";
import Widgets from "layouts/pages/widgets";
import RTL from "layouts/pages/rtl";
import Charts from "layouts/pages/charts";
import Notifications from "layouts/notifications";
import Kanban from "layouts/applications/kanban";
import Wizard from "layouts/applications/wizard";
import DataTables from "layouts/applications/data-tables";
import Calendar from "layouts/applications/calendar";
// import NewProduct from "layouts/ecommerce/products/new-product";
// import EditProduct from "layouts/ecommerce/products/edit-product";
// import ProductPage from "layouts/ecommerce/products/product-page";
import OrderList from "layouts/ecommerce/orders/order-list";
import OrderDetails from "layouts/ecommerce/orders/order-details";
import SignIn from "layouts/authentication/sign-in";
import ResetPassword from "layouts/authentication/reset-password";
import SignUp from "layouts/authentication/sign-up";
import UserList from "layouts/users/user-list";
import EditUser from "layouts/users/edit-user";

import NewProgram from "layouts/programs/new-program";
import EditProgram from "layouts/programs/edit-program";
import ProgramList from "layouts/programs/program-list";

import SessionsList from "layouts/programs/sessions-list";
import AddSession from "layouts/programs/add-session/AddSession";
import EditSession from "layouts/programs/sessions-list/EditSession";

import SoundscapeList from "layouts/soundscapes/soundscape-list";
import NewSoundscape from "layouts/soundscapes/new-soundscape";
import EditSoundscape from "layouts/soundscapes/edit-soundscape";

import SleepList from "layouts/sleep/sleep-list";
import NewSleep from "layouts/sleep/new-sleep";
import EditSleep from "layouts/sleep/edit-sleep";

import BreatheList from "layouts/breathe/breathe-list";
import NewBreathe from "layouts/breathe/new-breathe";
import EditBreathe from "layouts/breathe/edit-breathe";

import QuoteList from "layouts/library/quote-list";
import EditQuote from "layouts/library/quote-list/EditQuote";

import EditQuestion from "layouts/library/question-list/EditQuestion";
import QuestionList from "layouts/library/question-list";

import ArticleList from "layouts/articles/article-list";
import NewArticle from "layouts/articles/new-article";
import EditArticle from "layouts/articles/edit-article";

// MindPlus components
import MDAvatar from "components/MDAvatar";

// @mui icons
import Icon from "@mui/material/Icon";

// Images
import profilePicture from "assets/images/team-3.jpg";
import DoctorList from "layouts/patients/doctor-list";
import IPDPatientList from "layouts/patients/ipd-patient-list";
import OPDPatientList from "layouts/patients/opd-patient-list";
import EditPatient from "layouts/patients/edit-patient";

const routes = [
  {
    type: "collapse",
    name: "Dashboard",
    icon: <Icon fontSize="medium">dashboard</Icon>,
    key: "dashboard",
    route: "/dashboard",
    component: <Dashboard />,
    noCollapse: true,
  },
  // {
  //   type: "collapse",
  //   name: "Logout",
  //   key: "logout",
  //   icon: <Icon fontSize="medium">logout</Icon>,
  //   route: "/authentication/sign-in",
  //   component: <SignIn />,
  //   noCollapse: true,
  // },
  {
    type: "collapse",
    name: "Programs",
    key: "programs",
    icon: <Icon fontSize="medium">source</Icon>,
    collapse: [
      {
        name: "Programs",
        key: "program-list",
        route: "/programs/program-list",
        component: <ProgramList />,
      },
      {
        name: "Soundscapes",
        key: "soundscape-list",
        route: "/soundscapes/soundscape-list",
        component: <SoundscapeList />,
      },
      {
        name: "Sleep",
        key: "sleep-list",
        route: "/sleep/sleep-list",
        component: <SleepList />,
      },
      {
        name: "Breathe",
        key: "breathe-list",
        route: "/breathe/breathe-list",
        component: <BreatheList />,
      },
    ],
  },

  {
    type: "collapse",
    name: "Library",
    key: "library",
    icon: <Icon fontSize="medium">local_library</Icon>,
    collapse: [
      {
        name: "Quotes",
        key: "quotes-list",
        route: "/library/quote-list",
        component: <QuoteList />,
      },
      {
        name: "Questions",
        key: "questions-list",
        route: "/library/questions-list",
        component: <QuestionList />,
      },
      {
        name: "Articles",
        key: "article-list",
        route: "/articles/article-list",
        component: <ArticleList />,
      },
    ],
  },

  {
    type: "collapse",
    name: "Patients",
    key: "patients",
    icon: <Icon fontSize="medium">medical_services</Icon>,
    collapse: [
      {
        name: "Doctors",
        key: "doctor-list",
        route: "/patients/doctor-list",
        component: <DoctorList />,
      },
      {
        name: "IPD",
        key: "ipd-patient-list",
        route: "/patients/ipd-patient-list",
        component: <IPDPatientList />,
      },
      {
        name: "OPD",
        key: "opd-patient-list",
        route: "/patients/opd-patient-list",
        component: <OPDPatientList />,
      },
    ],
  },
  // {
  //   type: "collapse",
  //   name: "Programs",
  //   key: "program-list",
  //   route: "/programs/program-list",
  //   icon: <Icon fontSize="medium">apps</Icon>,
  //   component: <ProgramList />,
  //   noCollapse: true,
  // },
  {
    type: "collapse",
    name: "Users",
    key: "user-list",
    route: "/users/user-list",
    icon: <Icon fontSize="medium">groups</Icon>,
    component: <UserList />,
    noCollapse: true,
  },
  {
    type: "hidden",
    name: "New Article",
    key: "new-article",
    route: "/articles/new-article",
    component: <NewArticle />,
  },
  {
    type: "hidden",
    name: "Edit Article",
    key: "edit-article",
    route: "/articles/edit-article/:id",
    component: <EditArticle />,
  },
  {
    type: "hidden",
    name: "Edit Patient",
    key: "edit-patient",
    route: "/patients/edit-patient/:id",
    component: <EditPatient />,
  },
  {
    type: "hidden",
    name: "New Soundscape",
    key: "new-soundscape",
    route: "/soundscapes/new-soundscape",
    component: <NewSoundscape />,
  },
  {
    type: "hidden",
    name: "Edit Soundscape",
    key: "edit-soundscape",
    route: "/soundscapes/edit-soundscape/:id",
    component: <EditSoundscape />,
  },
  {
    type: "hidden",
    name: "Edit Question",
    key: "edit-question",
    route: "/library/edit-question/:id",
    component: <EditQuestion />,
  },
  {
    type: "hidden",
    name: "Edit Quote",
    key: "edit-quote",
    route: "/library/edit-quote/:id",
    component: <EditQuote />,
  },
  {
    type: "hidden",
    name: "New Breathe",
    key: "new-breathe",
    route: "/breathe/new-breathe",
    component: <NewBreathe />,
  },
  {
    type: "hidden",
    name: "Edit Breathe",
    key: "edit-breathe",
    route: "/breathe/edit-breathe/:id",
    component: <EditBreathe />,
  },
  {
    type: "hidden",
    name: "New Sleep",
    key: "new-sleep",
    route: "/sleep/new-sleep",
    component: <NewSleep />,
  },
  {
    type: "hidden",
    name: "Edit Sleep",
    key: "edit-sleep",
    route: "/sleep/edit-sleep/:id",
    component: <EditSleep />,
  },
  {
    type: "hidden",
    name: "New Program",
    key: "new-program",
    route: "/programs/new-program",
    component: <NewProgram />,
  },
  {
    type: "hidden",
    name: "Edit Program",
    key: "edit-program",
    route: "/programs/edit-program/:id",
    component: <EditProgram />,
  },
  {
    type: "hidden",
    name: "Edit Session",
    key: "edit-session",
    route: "/programs/edit-session/:id",
    component: <EditSession />,
  },
  {
    type: "hidden",
    name: "All Sessions",
    key: "sessions-list",
    route: "/programs/sessions-list/:id",
    component: <SessionsList />,
  },
  {
    type: "hidden",
    name: "Add Session",
    key: "add-session",
    route: "/programs/add-session",
    component: <AddSession />,
  },
  {
    type: "hidden",
    name: "New User",
    key: "new-user",
    route: "/users/new-user",
    component: <NewUser />,
  },
  {
    type: "hidden",
    name: "Edit User",
    key: "edit-user",
    route: "/users/edit-user/:email",
    component: <EditUser />,
  },
  {
    type: "hidden",
    name: "Settings",
    key: "settings",
    route: "/users/settings",
    component: <Settings />,
  },
  // { type: "divider", key: "divider-0" },
  // { type: "title", title: "Pages", key: "title-pages" },

  {
    type: "hidden",
    name: "Authentication",
    key: "authentication",
    icon: <Icon fontSize="medium">content_paste</Icon>,
    collapse: [
      {
        name: "Sign In",
        key: "sign-in",
        collapse: [
          {
            name: "Cover",
            key: "cover",
            route: "/authentication/sign-in",
            component: <SignIn />,
          },
        ],
      },
      {
        name: "Sign Up",
        key: "sign-up",
        collapse: [
          {
            name: "Cover",
            key: "cover",
            route: "/authentication/sign-up",
            component: <SignUp />,
          },
        ],
      },
      {
        name: "Reset Password",
        key: "reset-password",
        collapse: [
          {
            name: "Cover",
            key: "cover",
            route: "/authentication/reset-password",
            component: <ResetPassword />,
          },
        ],
      },
    ],
  },
];

export default routes;
